export function getTemplateQuestionDictionary(template) {
  return dictionaryFromQuestionSets(template?.assessment?.question_sets);
}

export function dictionaryFromCatalog(catalog) {
  return dictionaryFromQuestionSets(catalog?.question_sets);
}

export function dictionaryFromQuestionSets(questionSets) {
  return (questionSets ?? []).reduce((qsAcc, qs) => {
    if (!qs?.key) {
      return qsAcc;
    }

    return {
      ...qsAcc,
      [qs.key]: {
        name: qs.name,
        questions: (qs.questions ?? []).reduce((qAcc, q) => {
          if (!q?.key) {
            return qAcc;
          }

          return {
            ...qAcc,
            [q.key]: {
              name: q.text ?? q.name,
            },
          };
        }, {}),
      },
    };
  }, {});
}

function scoreTimestamp(value) {
  if (value == null || value === '') {
    return 0;
  }

  const parsed = Date.parse(value);
  return Number.isFinite(parsed) ? parsed : 0;
}

export function dictionaryFromQuestionScores(questionScores) {
  const sets = {};

  for (const row of questionScores ?? []) {
    for (const score of row.scores ?? []) {
      const qsKey = score.question_set_key;
      const qKey = score.question_key;
      if (!qsKey || !qKey) {
        continue;
      }

      const date = scoreTimestamp(score.assessment_date);
      const existingSet = sets[qsKey] ?? {
        name: undefined,
        _date: -1,
        questions: {},
      };
      const keepIncomingSet = date >= existingSet._date;
      const setName = keepIncomingSet
        ? score.question_set_name || existingSet.name
        : existingSet.name || score.question_set_name;
      const setDate = keepIncomingSet ? date : existingSet._date;

      const existingQuestion = existingSet.questions[qKey];
      const questionDate = existingQuestion?._date ?? -1;
      const keepIncomingQuestion = !existingQuestion || date >= questionDate;

      sets[qsKey] = {
        name: setName,
        _date: setDate,
        questions: {
          ...existingSet.questions,
          [qKey]: keepIncomingQuestion
            ? {
                name: score.question_text || existingQuestion?.name,
                _date: date,
              }
            : existingQuestion,
        },
      };
    }
  }

  return Object.fromEntries(
    Object.entries(sets).map(([qsKey, qs]) => [
      qsKey,
      {
        name: qs.name,
        questions: Object.fromEntries(
          Object.entries(qs.questions).map(([qKey, q]) => [
            qKey,
            { name: q.name },
          ])
        ),
      },
    ])
  );
}

function overlayDictionary(base, overlay) {
  const next = { ...base };

  for (const [qsKey, qs] of Object.entries(overlay ?? {})) {
    const existing = next[qsKey] ?? { name: undefined, questions: {} };
    const questions = { ...existing.questions };

    for (const [qKey, question] of Object.entries(qs.questions ?? {})) {
      const incomingName = question?.name;
      questions[qKey] = {
        name: incomingName || questions[qKey]?.name,
      };
    }

    next[qsKey] = {
      name: qs.name || existing.name,
      questions,
    };
  }

  return next;
}

export function mergeQuestionDictionaries(...dictionaries) {
  return dictionaries.filter(Boolean).reduce(overlayDictionary, {});
}

export function buildRecommendationsDictionary(
  template,
  catalog,
  questionScores
) {
  return mergeQuestionDictionaries(
    getTemplateQuestionDictionary(template),
    dictionaryFromQuestionScores(questionScores),
    dictionaryFromCatalog(catalog)
  );
}

export function lookupNameInDictionary(
  dictionary,
  questionSetKey,
  questionKey = null
) {
  const name =
    questionKey === null
      ? dictionary?.[questionSetKey]?.name
      : dictionary?.[questionSetKey]?.questions?.[questionKey]?.name;

  return name || questionKey || questionSetKey;
}

export function uniqueQuestionSetKeys(catalog, template, scores) {
  const keys = new Set();

  for (const qs of catalog?.question_sets ?? []) {
    if (qs?.key) {
      keys.add(qs.key);
    }
  }

  for (const qs of template?.assessment?.question_sets ?? []) {
    if (qs?.key) {
      keys.add(qs.key);
    }
  }

  for (const key of Object.keys(scores ?? {})) {
    keys.add(key);
  }

  return keys;
}
